<template>
  <div
    id="giftcard"
    data-anchor="giftcard"
    :class="$b()"
  >
    <BaseHeading
      tag="h2"
      size="h1"
      :class="$b('heading')"
    >
      E-Gift Cards.
    </BaseHeading>

    <div :class="$b('giftcard-container')">
      <div :class="$b('image-outer')">
        <img
          src="https://www.datocms-assets.com/45158/1699005413-giftcard22.png?auto=format&w=400"
          alt="SunGod E-Gift Card"
          :class="$b('image')"
        />

        <BaseCopy
          tag="p"
          size="body-sm"
          :class="$b('sub-text')"
        >
          E-Gift Card redeemable at
          <strong>www.sungod.co</strong>
        </BaseCopy>
      </div>

      <div :class="$b('product-panel')">
        <p
          :class="$b('text')"
        >
          Give a SunGod E-Gift Card, so they can See Better with a product of
          their choice.
        </p>

        <template v-if="isValidLocale">
          <div :class="$b('product-input')">
            <VisionSelect
              id="gift-card-value"
              v-model="selectedGiftCardValue"
              name="gift-card-value"
              label="E-Gift Card Value"
              :options="giftCardOptions"
            />
          </div>
  
          <GiftingButton
            grow
            :disabled="!selectedGiftCardValue || loading"
            :class="$b('add-to-cart')"
            @click="addToBasket"
          >
            Add to basket
          </GiftingButton>
  
          <div :class="$b('product-info')">
            <VisionIcon
              :class="$b('product-info-icon')"
              icon="gift"
              colour="black"
              size="small"
            />
            <p
              :class="$b('product-info-text')"
            >
              Delivered to your email immediately after purchase as a printable
              PDF, ready for you to gift. Valid for 12 months.
            </p>
          </div>
        </template>

        <template v-else>
          <p
            :class="$b('text')"
          >
            Sorry, E-Gift Cards are only available for purchase from our United Kingdom store at this time.
          </p>

          <a
            href="/en-gb/gifts"
          >
            <SecondaryButton>
              Shop in £
            </SecondaryButton>
          </a>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
export default defineNuxtComponent({
  name: 'GiftVoucherBlock',

  setup() {
    const { resolveLocalePath } = useUrlBuilder()
    const shopifyStore = useShopifyStore()

    return { resolveLocalePath, shopifyStore }
  },

  data: () => ({
    selectedGiftCardValue: null,
    giftCardOptions: [],
    loading: false
  }),

  computed: {
    isValidLocale () {
      return this.shopifyStore.currencyCode === 'GBP'
    }
  },

  async setup() {
    const { resolveLocalePath } = useUrlBuilder()
    const { $product, $tcs } = useNuxtApp()
    const shopifyStore = useShopifyStore()
    const selectedGiftCardValue = ref(null)
    const giftCardOptions = ref([])
    const loading = ref(false)

    const isValidLocale = computed(() => shopifyStore.currencyCode === 'GBP')

    const customisedProduct = await $product.getCustomisedProduct('egiftcard', {
      sku: 'egiftcard',
    })

    const giftCardResponse = await shopifyStore.getGiftCardProduct(
      customisedProduct.product.commerceId
    )

    giftCardOptions.value = giftCardResponse?.giftCardOptions?.map(giftCardOption => {
      return {
        value: giftCardOption.id,
        monetaryValue: Number(giftCardOption.price.amount),
        displayValue: $tcs(Number(giftCardOption.price.amount))
      }
    })

    selectedGiftCardValue.value = giftCardOptions.value[0].value

    return {
      resolveLocalePath,
      shopifyStore,
      selectedGiftCardValue,
      giftCardOptions,
      loading,
      isValidLocale
    }
  },
  
  methods: {
    async addToBasket() {
      try {
        this.loading = true
        const success = await this.shopifyStore.addCartItems([
          {
            merchandiseId: this.selectedGiftCardValue,
            quantity: 1,
            attributes: []
          },
        ])

        if (success) this.shopifyStore.toggleCart(true)

      } catch (error) {
        this.$sentry.captureException(error)
      }

      this.loading = false
    },

    setValue (value) {
      const nextLargest = this.giftCardOptions.find(option => option.monetaryValue >= value) || this.giftCardOptions.slice(-1)[0]
      console.log(value, nextLargest.value)
      this.selectedGiftCardValue = nextLargest.value
    }
  },
})
</script>

<style lang="postcss">
.GiftVoucherBlock {
  @apply pb-md md:pb-lg;
}

.GiftVoucherBlock__heading {
  @apply mb-md
    inline-block;

  background-image: var(--gradient-gold);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.GiftVoucherBlock__text {
  @apply mb-sm text-base;
}

.GiftVoucherBlock__giftcard-container {
  @apply flex flex-col md:flex-row items-center md:items-stretch;
}

.GiftVoucherBlock__image-outer {
  @apply px-sm md:px-md py-lg md:py-xxl mb-md md:mb-0 md:mr-xs
    flex-grow
    relative
    w-full
    bg-grey-light;
  border-radius: 10px;

  @screen md {
    flex: 1 1 50%;
  }

  @screen lg {
    flex-basis: 66%;
  }
}

.GiftVoucherBlock__sub-text {
  @apply mt-md text-center;
}

.GiftVoucherBlock__image {
  @apply w-1/2
    mx-auto
    relative;

  min-width: 160px;
  max-width: 256px;
  /* -webkit-box-shadow: 0px 16px 8px -10px rgba(0,0,0,0.32);
  -moz-box-shadow: 0px 16px 8px -10px rgba(0,0,0,0.32);
  box-shadow: 0px 16px 8px -10px rgba(0,0,0,0.32); */
}

.GiftVoucherBlock__product-panel {
  @apply md:ml-xs
    flex justify-center flex-col
    w-full;

  @screen md {
    flex: 1 1 50%;
  }

  @screen lg {
    flex-basis: 33%;
  }
}

.GiftVoucherBlock__product-heading {
  @apply mb-xxxs;
}

.GiftVoucherBlock__product-input {
  @apply mb-sm
    w-full;

  .TextInput__input {
    @apply w-full;
  }
}

.GiftVoucherBlock__product-checkbox {
  @apply mb-md pl-xs md:pl-sm
    flex justify-between
    w-full;

  margin-left: -2px;
}

.GiftVoucherBlock__checkbox-text {
  @apply inline-block;
  max-width: 215px;

  @screen sm {
    max-width: 100%;
  }
  @screen md {
    max-width: 260px;
  }
}

.GiftVoucherBlock__product-info {
  @apply p-xs md:p-sm
    flex items-center
    rounded-md;
}

.GiftVoucherBlock__product-info-icon {
  margin-right: 20px;
}

.GiftVoucherBlock__product-info-text {
  @apply text-xs;
}
</style>
